// Server-side check for mission (lab) completions — phase 2 of server-verified progress.
//
// The browser can only *claim* that a mission is complete. The server rebuilds the lab from
// scratch with the same seed (so randomly injected faults are the same), then replays the
// learner's journal (src/core/runtime.js): every console command, every change made in the
// adapter dialog or the cable inspector (src/core/labOps.js), every activity the learner submitted
// (each widget's own verify() rule, checked against the replayed network at that moment), every
// hint and every VERIFY press. The mission counts only when that replay completes the lab with the
// normal stage checks. Nothing the browser says about its own state (evidence, "completed",
// stored output, the network) is trusted.
//
// It runs in two places with the same code:
//   * the Supabase Edge Function supabase/functions/verify-lab (bundled by
//     scripts/build-functions.mjs into supabase/functions/_shared/lab-verify.js), which writes the
//     result into public.completions with the service role — learners cannot write that table;
//   * tests (tests/labVerify.test.mjs, tests/fakeSupabase.mjs, the D01 e2e run).
// It has no browser or Node dependencies.
import { LabRuntime, MAX_JOURNAL } from './runtime.js';
import { xpWithHints, incidentXp } from './xp.js';
import { missionKind } from '../map/mapData.js';
import d01 from '../domains/d01/index.js';
import d02 from '../domains/d02/index.js';
import d03 from '../domains/d03/index.js';
import d04 from '../domains/d04/index.js';
import d05 from '../domains/d05/index.js';
import d06 from '../domains/d06/index.js';
import d07 from '../domains/d07/index.js';
import { INCIDENT_DOMAIN, findIncident } from '../incidents/index.js';

/** Domains whose missions are verified on the server — all seven networking domains. */
export const SERVER_LAB_DOMAINS = ['d01', 'd02', 'd03', 'd04', 'd05', 'd06', 'd07'];
const DOMAINS = { d01, d02, d03, d04, d05, d06, d07 };
export const MAX_LAB_BODY_BYTES = 768 * 1024;
const MAX_LINE = 500;
// runtime-internal actions a journal may not inject (they are produced by 'h' / 'v' entries)
const RESERVED_ACTIONS = new Set(['hint', 'verify', 'admin-reference-apply']);
const RESERVED_KEYS = new Set(['type', 'name', 't', 'stage', '__proto__', 'constructor', 'prototype']);

/** 'd01.u03' → { domain, unit, index } (only server-verified domains, implemented units). */
export function labUnit(key) {
  const m = /^(d\d\d)\.(u\d\d)$/.exec(String(key || ''));
  if (!m || !SERVER_LAB_DOMAINS.includes(m[1])) return null;
  const domain = DOMAINS[m[1]];
  const index = domain.units.findIndex((u) => u.id === m[2]);
  const unit = domain.units[index];
  return unit?.implemented ? { domain, unit, index } : null;
}

/** All server-verified mission keys, in path order. */
export const SERVER_LAB_KEYS = SERVER_LAB_DOMAINS.flatMap((d) => DOMAINS[d].units.filter((u) => u.implemented).map((u) => `${d}.${u.id}`));

/**
 * Incident Labs verified on the server (Phase 3). An explicit allow-list: a key is accepted only
 * when it is exactly 'inc.<one of these ids>'. There is no prefix rule — 'inc.anything-else',
 * 'inc.d01.u01', 'inc.ospf-adjacency.x', ' inc.ospf-adjacency' and so on are unknown. Incidents
 * are never missions. Phase 4: a verified incident is stored with incidentXp(hints) (src/core/xp.js),
 * once per account (one row per incident); it never counts as a curriculum completion.
 */
export const SERVER_INCIDENT_IDS = Object.freeze(['ospf-adjacency', 'vpn-connectivity', 'trunk-vlan', 'etherchannel', 'stp-root', 'hsrp-split', 'access-vlan', 'ospf-mtu', 'bgp-advertise', 'ipsec-traffic', 'qos-peak', 'acl-app', 'aaa-login', 'segmentation', 'snmp-traps', 'syslog-gap', 'ntp-sync', 'dc-capstone']);
/** 'inc.ospf-adjacency', … */
export const SERVER_INCIDENT_KEYS = Object.freeze(SERVER_INCIDENT_IDS.map((id) => `${INCIDENT_DOMAIN}.${id}`));
/** Every key the verify-lab function accepts: the curriculum missions, then the incidents. */
export const SERVER_VERIFY_KEYS = Object.freeze([...SERVER_LAB_KEYS, ...SERVER_INCIDENT_KEYS]);

/** 'inc.ospf-adjacency' → the incident (only an exact allow-listed key whose scenario is implemented). */
export function incidentTarget(key) {
  if (typeof key !== 'string' || !SERVER_INCIDENT_KEYS.includes(key)) return null;
  const inc = findIncident(key.slice(INCIDENT_DOMAIN.length + 1));
  return inc?.implemented && inc.incident ? inc : null;
}

/** A mission or an incident for a verify request, or null. Missions and incidents never overlap. */
function labTarget(key) {
  const mission = labUnit(key);
  if (mission) return { ...mission, incident: false };
  const inc = incidentTarget(key);
  return inc ? { domain: { id: INCIDENT_DOMAIN, units: [] }, unit: inc, index: -1, incident: true } : null;
}

/** XP for a verified mission with `hints` hints used (same rule as src/core/xp.js). */
export const labXp = (unit, hints) => xpWithHints(missionKind(unit), hints);

function memStore() {
  const m = new Map();
  return {
    get: (k, f = null) => (m.has(k) ? JSON.parse(m.get(k)) : f),
    set: (k, v) => { m.set(k, JSON.stringify(v)); },
    remove: (k) => { m.delete(k); },
    keys: () => [...m.keys()],
    onChange: () => () => {},
  };
}

/** Plain JSON-safe copy (drops functions, prototypes; bounded size). */
function plain(v, depth = 0) {
  if (v === null || typeof v === 'boolean' || typeof v === 'number') return Number.isFinite(v) || typeof v !== 'number' ? v : null;
  if (typeof v === 'string') return v.slice(0, 4000);
  if (depth > 6) return null;
  if (Array.isArray(v)) return v.slice(0, 200).map((x) => plain(x, depth + 1));
  if (typeof v === 'object') {
    const o = {};
    for (const [k, x] of Object.entries(v).slice(0, 200)) if (!['__proto__', 'constructor', 'prototype'].includes(k)) o[k] = plain(x, depth + 1);
    return o;
  }
  return null;
}
const actionPayload = (p) => {
  const o = plain(p && typeof p === 'object' && !Array.isArray(p) ? p : {});
  for (const k of RESERVED_KEYS) delete o[k];
  return o;
};

/**
 * Replay one mission.
 * @param {string} key        'd01.u03'
 * @param {object} lab        { v, seed, forced?, journal } from the learner's lab record (untrusted)
 * @param {string[]} verified mission keys already verified for this account
 * @returns {{ ok:boolean, reason?:string, at?:number, hints?:number, xp?:number }}
 */
export function verifyLabRecord(key, lab, verified = []) {
  const target = labTarget(key);
  if (!target) return { ok: false, reason: 'unknown-mission' };
  const { domain, unit, index } = target;
  // missions are sequential: every earlier mission of the domain must already be verified.
  // Incidents have no server prerequisite (they are outside the ordered curriculum); the client
  // keeps its own local unlock rule.
  const done = new Set(Array.isArray(verified) ? verified : []);
  const missing = target.incident ? [] : domain.units.slice(0, index).filter((u) => u.implemented && !done.has(`${domain.id}.${u.id}`));
  if (missing.length) return { ok: false, reason: 'prereq', waitingFor: `${domain.id}.${missing[0].id}` };

  if (!lab || typeof lab !== 'object' || !Array.isArray(lab.journal)) return { ok: false, reason: 'no-journal' };
  if (lab.journalOverflow) return { ok: false, reason: 'journal-too-long' };
  if (lab.v !== (unit.version || 1)) return { ok: false, reason: 'outdated' };
  if (!Number.isInteger(lab.seed) || lab.seed <= 0 || lab.seed > 0xFFFFFFFF) return { ok: false, reason: 'no-journal' };
  if (lab.journal.length > MAX_JOURNAL) return { ok: false, reason: 'journal-too-long' };

  let completion = null;
  const progress = { store: null, markComplete: (d, u, meta) => { completion = meta || {}; return true; } };
  const st = memStore();
  progress.store = st;
  let rt;
  try {
    rt = new LabRuntime(domain.id, unit, progress, { st });
    rt.state.seed = lab.seed >>> 0;
    rt.state.rng = 0;
    if (lab.forced !== undefined && lab.forced !== null) {
      if (!unit.validForced?.(lab.forced)) return { ok: false, reason: 'invalid-setup' };
      rt.replayForced = plain(lab.forced);
    }
    rt.init();
    for (const s of unit.stages) for (const w of s.widgets || []) w.headless?.(rt);
  } catch (e) {
    return { ok: false, reason: 'setup-failed', detail: String(e?.message || e).slice(0, 200) };
  }

  try {
    for (const e of lab.journal) {
      if (rt.state.completed) break;
      if (!e || typeof e !== 'object') return { ok: false, reason: 'bad-entry' };
      if (e.k === 'x') {
        if (typeof e.d !== 'string' || typeof e.l !== 'string' || e.l.length > MAX_LINE || !rt.net?.devices?.[e.d]) return { ok: false, reason: 'bad-command' };
        rt.exec(e.d, e.l);
      } else if (e.k === 'a') {
        if (typeof e.n !== 'string' || e.n.length > 40 || RESERVED_ACTIONS.has(e.n)) continue;
        rt.action(e.n, actionPayload(e.p));
      } else if (e.k === 'o') {
        if (!rt.op(String(e.n), actionPayload(e.p))) return { ok: false, reason: 'bad-change' };
      } else if (e.k === 'w') {
        const def = (rt.stage?.widgets || []).find((w) => w.id === e.id);
        if (!def) return { ok: false, reason: 'bad-activity' };
        if (rt.widget(def.id).done) continue;
        if (def.selfCompleting) return { ok: false, reason: 'activity-not-done', widget: def.id };
        const data = plain(e.data && typeof e.data === 'object' ? e.data : {});
        let ok = false;
        try { ok = typeof def.verify === 'function' && def.verify(data, rt) === true; } catch { ok = false; }
        if (!ok) return { ok: false, reason: 'activity-rejected', widget: def.id };
        rt.widget(def.id).data = data;
        rt.widgetDone(def.id);
      } else if (e.k === 'h') {
        if (rt.hintsShown() < (rt.stage?.hints || []).length) rt.revealHint();
      } else if (e.k === 'v') {
        rt.verify();
      } else return { ok: false, reason: 'bad-entry' };
    }
  } catch (err) {
    return { ok: false, reason: 'replay-failed', detail: String(err?.message || err).slice(0, 200) };
  }
  if (!rt.state.completed || !completion || completion.bypass) return { ok: false, reason: 'not-complete', stage: rt.stage?.id || null };
  const hints = Math.max(0, Number(completion.hints) || 0);
  if (target.incident) return { ok: true, hints, xp: incidentXp(hints), incident: true };
  return { ok: true, hints, xp: labXp(unit, hints) };
}

/** The part of a lab record the browser sends (keeps requests small). */
export const labPayload = (lab) => (lab && typeof lab === 'object'
  ? { v: lab.v, seed: lab.seed, forced: lab.forced ?? null, journal: lab.journal, journalOverflow: !!lab.journalOverflow }
  : null);

/**
 * One Edge Function request → response. Pure; the function adds auth and storage.
 * @param {{ userId: string|null, bodyText: string, verified: string[] }} o
 * @returns {{ status:number, body:object, insert: object[] }}
 */
export function decideLabRequest({ userId, bodyText, verified }) {
  if (!userId) return { status: 401, body: { error: 'Sign in first.' }, insert: [] };
  if (typeof bodyText !== 'string' || bodyText.length > MAX_LAB_BODY_BYTES) return { status: 413, body: { error: 'Request too large.' }, insert: [] };
  let body;
  try { body = JSON.parse(bodyText); } catch { return { status: 400, body: { error: 'Invalid JSON.' }, insert: [] }; }
  const key = typeof body?.mission === 'string' ? body.mission : ''; // never coerce (no ['d01.u01'] → 'd01.u01')
  const already = (Array.isArray(verified) ? verified : []).filter((k) => SERVER_VERIFY_KEYS.includes(k));
  if (already.includes(key)) return { status: 200, body: { mission: key, ok: true, already: true, verified: already }, insert: [] };
  const r = verifyLabRecord(key, body?.lab, already);
  const all = r.ok ? [...already, key] : already;
  // a verified incident is stored as kind 'lab', item_id 'inc.<id>' with its incident XP (first
  // verified solve only: an already-verified key returns above and inserts nothing)
  const insert = !r.ok ? [] : r.incident
    ? [{ user_id: userId, kind: 'lab', item_id: key, xp: r.xp, details: { hints: r.hints, incident: true } }]
    : [{ user_id: userId, kind: 'lab', item_id: key, xp: r.xp, details: { hints: r.hints } }];
  return { status: 200, body: { mission: key, ...r, verified: all }, insert };
}
